// src/hooks/useQuestionTimer.js
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
const API_BASE_URL = process.env.REACT_APP_API_URL || "";

export default function useQuestionTimer({
  token,
  sessionId,
  nivelId,
  temaId,
  isFinal = false, // <<--- NUEVO
}) {
  const [preguntaId, setPreguntaId] = useState(null);
  const startedAtRef = useRef(null);
  const accumMsRef = useRef(0);
  const tickingRef = useRef(false);
  const lastTickRef = useRef(null);

  const tick = () => {
    if (!tickingRef.current || !lastTickRef.current) return;
    const now = Date.now();
    accumMsRef.current += now - lastTickRef.current;
    lastTickRef.current = now;
  };

  const pause = useCallback(() => {
    tick();
    tickingRef.current = false;
  }, []);

  const resume = useCallback(() => {
    lastTickRef.current = Date.now();
    tickingRef.current = true;
  }, []);

  // Nueva pregunta
  const startQuestion = useCallback(
    (newPreguntaId) => {
      if (preguntaId != null) {
        console.warn(
          "[useQuestionTimer] startQuestion() con pregunta previa sin cerrar; se descarta la anterior",
          { prev: preguntaId, next: newPreguntaId }
        );
      }
      setPreguntaId(Number(newPreguntaId));
      startedAtRef.current = new Date();
      accumMsRef.current = 0;
      resume();
    },
    [preguntaId, resume]
  );

  // Finalizar y guardar
  const endQuestion = useCallback(async () => {
    if (preguntaId == null || !startedAtRef.current) return;

    pause();

    const toPosInt = (v) => {
      const n = Number(v);
      return Number.isFinite(n) && n > 0 ? n : null;
    };

    const ids = {
      sessionId: toPosInt(sessionId),
      nivelId: toPosInt(nivelId),
      temaId: temaId == null ? null : toPosInt(temaId), // permite null en final
      preguntaId: toPosInt(preguntaId),
    };

    const payload = {
      sessionId: ids.sessionId ?? 0,
      nivelId: ids.nivelId ?? 0, // si 0, el backend puede inferir
      temaId: isFinal ? null : ids.temaId ?? 0, // FINAL: forzamos null
      preguntaId: ids.preguntaId ?? 0,
      startedAt: startedAtRef.current.toISOString(),
      endedAt: new Date().toISOString(),
      elapsedMs: Math.max(0, Math.round(accumMsRef.current)),
      isFinal, // <<--- NUEVO
    };

    // Validación mínima (temaId no es obligatorio si es final)
    const missing = [];
    if (!payload.sessionId) missing.push("sessionId");
    if (!payload.preguntaId) missing.push("preguntaId");
    if (!payload.startedAt) missing.push("startedAt");
    if (!payload.endedAt) missing.push("endedAt");
    if (payload.elapsedMs == null) missing.push("elapsedMs");
    if (!token) missing.push("token");
    if (!isFinal && !(ids.nivelId || ids.temaId))
      missing.push("nivelId|temaId");

    if (missing.length) {
      console.warn(
        "[useQuestionTimer] Campos inválidos, no se envía a /api/tiempos/pregunta",
        { missing, payload }
      );
      // reset
      setPreguntaId(null);
      startedAtRef.current = null;
      accumMsRef.current = 0;
      tickingRef.current = false;
      lastTickRef.current = null;
      return;
    }

    try {
      await axios.post(`${API_BASE_URL}/api/tiempos/pregunta`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (e) {
      console.error("save question time failed", e, { payload });
    } finally {
      // reset
      setPreguntaId(null);
      startedAtRef.current = null;
      accumMsRef.current = 0;
      tickingRef.current = false;
      lastTickRef.current = null;
    }
  }, [pause, preguntaId, sessionId, nivelId, temaId, isFinal, token]);

  useEffect(() => {
    const onVis = () => (document.hidden ? pause() : resume());
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [pause, resume]);

  return { startQuestion, endQuestion, pause, resume };
}
